/**
 * IANA time zones where trackers stay off until the user opts in: the EU and
 * EEA (Norway, Iceland, Liechtenstein, including EU outermost regions), the UK
 * and Crown Dependencies, Quebec, India, China, South Korea, Thailand,
 * Vietnam, Indonesia, Turkey, Colombia, Peru, Chile, Argentina, Serbia,
 * Montenegro, Albania, Nigeria, Oman, the UAE, Jordan, Egypt and Israel.
 * Legacy aliases are listed beside canonical names because browsers report
 * whichever the OS is set to.
 *
 * A zone shared with a neighbour counts as opt-in: `America/Toronto` covers
 * Quebec and Ontario, `Europe/Zurich` covers Liechtenstein and Switzerland,
 * `Asia/Bangkok` covers Thailand, Cambodia and Laos, `Asia/Dubai` covers the
 * UAE and Oman, `Africa/Lagos` covers Nigeria and much of West and Central
 * Africa.
 */
const OPT_IN_TIME_ZONES = new Set([
  // EU
  "Europe/Vienna",
  "Europe/Brussels",
  "Europe/Sofia",
  "Europe/Zagreb",
  "Asia/Nicosia",
  "Asia/Famagusta",
  "Europe/Nicosia",
  "Europe/Prague",
  "Europe/Copenhagen",
  "Europe/Tallinn",
  "Europe/Helsinki",
  "Europe/Mariehamn",
  "Europe/Paris",
  "America/Cayenne",
  "America/Guadeloupe",
  "America/Marigot",
  "America/Martinique",
  "Indian/Mayotte",
  "Indian/Reunion",
  "Europe/Berlin",
  "Europe/Busingen",
  "Europe/Athens",
  "Europe/Budapest",
  "Europe/Dublin",
  "Eire",
  "Europe/Rome",
  "Europe/Riga",
  "Europe/Vilnius",
  "Europe/Luxembourg",
  "Europe/Malta",
  "Europe/Amsterdam",
  "Europe/Warsaw",
  "Poland",
  "Europe/Lisbon",
  "Atlantic/Azores",
  "Atlantic/Madeira",
  "Portugal",
  "Europe/Bucharest",
  "Europe/Bratislava",
  "Europe/Ljubljana",
  "Europe/Madrid",
  "Africa/Ceuta",
  "Atlantic/Canary",
  "Europe/Stockholm",
  // EEA
  "Europe/Oslo",
  "Arctic/Longyearbyen",
  "Atlantic/Jan_Mayen",
  "Atlantic/Reykjavik",
  "Iceland",
  "Europe/Vaduz",
  "Europe/Zurich",
  // UK and Crown Dependencies
  "Europe/London",
  "Europe/Belfast",
  "GB",
  "GB-Eire",
  "Europe/Guernsey",
  "Europe/Isle_of_Man",
  "Europe/Jersey",
  // Quebec
  "America/Toronto",
  "America/Montreal",
  "America/Blanc-Sablon",
  "Canada/Eastern",
  // India
  "Asia/Kolkata",
  "Asia/Calcutta",
  // China
  "Asia/Shanghai",
  "Asia/Urumqi",
  "Asia/Chongqing",
  "Asia/Chungking",
  "Asia/Harbin",
  "Asia/Kashgar",
  "PRC",
  // South Korea
  "Asia/Seoul",
  "ROK",
  // Thailand
  "Asia/Bangkok",
  // Vietnam
  "Asia/Ho_Chi_Minh",
  "Asia/Saigon",
  // Indonesia
  "Asia/Jakarta",
  "Asia/Pontianak",
  "Asia/Makassar",
  "Asia/Ujung_Pandang",
  "Asia/Jayapura",
  // Turkey
  "Europe/Istanbul",
  "Asia/Istanbul",
  "Turkey",
  // Colombia
  "America/Bogota",
  // Peru
  "America/Lima",
  // Chile
  "America/Santiago",
  "America/Punta_Arenas",
  "Pacific/Easter",
  "Chile/Continental",
  "Chile/EasterIsland",
  // Argentina (current names share the America/Argentina/ prefix)
  "America/Buenos_Aires",
  "America/Catamarca",
  "America/Cordoba",
  "America/Jujuy",
  "America/Mendoza",
  "America/Rosario",
  // Serbia, Montenegro, Albania
  "Europe/Belgrade",
  "Europe/Podgorica",
  "Europe/Tirane",
  // Nigeria
  "Africa/Lagos",
  // Oman, UAE
  "Asia/Muscat",
  "Asia/Dubai",
  // Jordan
  "Asia/Amman",
  // Egypt
  "Africa/Cairo",
  "Egypt",
  // Israel
  "Asia/Jerusalem",
  "Asia/Tel_Aviv",
  "Israel",
]);

/**
 * Whether trackers start off for a browser in this time zone. The zone stands
 * in for where the user is and never leaves the browser. A missing, UTC or
 * `Etc/*` zone says nothing about location, so it starts off too.
 */
export function trackingOptInRequired(timeZone: string | undefined): boolean {
  if (!timeZone || timeZone === "UTC" || timeZone.startsWith("Etc/")) return true;
  return OPT_IN_TIME_ZONES.has(timeZone) || timeZone.startsWith("America/Argentina/");
}
